/**
 * Ordem das seções do Localizador.
 *
 * D3 da spec: **capability decide o que EXISTE; papel decide só a ORDEM.**
 * Este arquivo é o ÚNICO lugar do Spotlight onde papel aparece — em qualquer
 * outro, papel decidindo presença é violação de P19 (ADR-046/132).
 *
 * A semente resolve o dia 1; o uso do próprio usuário resolve o dia 30. O uso
 * só promove acima de `USAGE_PROMOTE_MIN` aberturas: abaixo disso é ruído, não
 * sinal. O limiar é arbitrado, não medido — não existe telemetria de busca no
 * app para calibrar (hipótese declarada na spec §9).
 */
import type { SpotlightSection, SpotlightSectionId } from './types';

// D5/T13 (prancha 03/3b): «Configurações» vem PRIMEIRO em toda semente — quem digita
// «failover» está configurando, não navegando. Quando o termo não casa config, a seção
// não existe (vazia colapsa) e a ordem de sempre segue intacta.
export const ROLE_SEED_ORDER: Record<string, SpotlightSectionId[]> = {
  atendente: ['config', 'people', 'messages', 'screens', 'documents'],
  supervisor: ['config', 'operators', 'people', 'messages', 'screens'],
  gestor: ['config', 'screens', 'people', 'operators', 'messages'],
  admin_tenant: ['config', 'screens', 'operators', 'people'],
  dpo_tenant: ['config', 'people', 'screens', 'documents'],
};

/**
 * Usada quando o papel não tem semente própria (papel novo, papel Lume, ou
 * `user` ainda carregando). Nunca deixa seção de fora.
 */
const FALLBACK_ORDER: SpotlightSectionId[] = [
  'config',
  'people',
  'screens',
  'operators',
  'documents',
  'messages',
];

export const USAGE_PROMOTE_MIN = 3;

/** Sempre no topo: quando o termo É um id, não há o que ranquear. */
const PINNED_TOP: SpotlightSectionId = 'conversation-id';

/** Peso de quem não está em nenhuma lista — vai para o fim, sem colidir. */
const UNRANKED = 1_000;

function seedIndexFor(seed: SpotlightSectionId[]): (id: SpotlightSectionId) => number {
  return (id) => {
    const inSeed = seed.indexOf(id);
    if (inSeed !== -1) return inSeed;
    const inFallback = FALLBACK_ORDER.indexOf(id);
    if (inFallback !== -1) return seed.length + inFallback;
    return UNRANKED;
  };
}

export function orderSections(
  sections: SpotlightSection[],
  role: string | undefined,
  usage: Record<string, number>,
): SpotlightSection[] {
  const seed = (role !== undefined ? ROLE_SEED_ORDER[role] : undefined) ?? FALLBACK_ORDER;
  const seedIndex = seedIndexFor(seed);

  // Seção sem item E sem `loading` não existe: cabeçalho de seção vazia gasta
  // duas linhas para informar nada (decisão da peça 01 da rodada de design).
  // `filter` já devolve array novo — o argumento nunca é mutado.
  const visible = sections.filter((s) => s.items.length > 0 || s.loading === true);

  return visible.sort((a, b) => {
    if (a.id === PINNED_TOP) return -1;
    if (b.id === PINNED_TOP) return 1;
    const ua = usage[a.id] ?? 0;
    const ub = usage[b.id] ?? 0;
    const promotedA = ua >= USAGE_PROMOTE_MIN ? 0 : 1;
    const promotedB = ub >= USAGE_PROMOTE_MIN ? 0 : 1;
    if (promotedA !== promotedB) return promotedA - promotedB;
    if (promotedA === 0 && ua !== ub) return ub - ua;
    return seedIndex(a.id) - seedIndex(b.id);
  });
}
